import React from 'react';
import { Box, Paper, Skeleton, Container } from '@mui/material';

/**
 * Skeleton loading component for SafeInvest page
 * Matches the exact layout of the real components
 */
const SafeInvestSkeleton = () => {
  return (
    <Box sx={{ minHeight: '100vh', py: 2 }}>
      <Container maxWidth="xl">
        {/* Header Skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton 
              variant="rounded" 
              width={40} 
              height={40} 
              sx={{ borderRadius: 2 }}
            />
            <Box>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="text" width={120} height={16} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: 1 }} />
        </Box>

        {/* Layer 1: Market Regime Card Skeleton */}
        <Paper sx={{ 
          mb: 2, 
          overflow: 'hidden', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
          borderRadius: 3 
        }}>
          {/* Header */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
              <Box>
                <Skeleton variant="text" width={100} height={14} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Skeleton variant="text" width={140} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Skeleton variant="circular" width={80} height={80} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Skeleton variant="text" width={50} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              </Box>
            </Box>
          </Box>
          
          {/* Score Bars */}
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ flex: '1 1 180px', minWidth: 150 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={30} height={16} />
                  </Box>
                  <Skeleton variant="rounded" height={8} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Box>
            
            {/* Derivatives Section */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Skeleton variant="text" width={150} height={18} />
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Skeleton variant="rounded" width={80} height={40} />
                <Skeleton variant="rounded" width={80} height={40} />
                <Skeleton variant="rounded" width={120} height={40} />
              </Box>
            </Box>
            
            {/* Action Box */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2 }}>
              <Skeleton variant="text" width={120} height={18} />
              <Skeleton variant="text" width="100%" height={16} />
              <Skeleton variant="text" width="80%" height={16} />
            </Box>
          </Box>
        </Paper>

        {/* Layer 2: Sector Rotation Skeleton */}
        <Paper sx={{ mb: 2, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={150} height={22} />
              <Skeleton variant="text" width={200} height={14} />
            </Box>
          </Box>

          {/* Top 3 Sectors Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 1.5, mb: 2 }}>
            {[1, 2, 3].map((i) => (
              <Box 
                key={i}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: '#f8f9ff',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width={28} height={28} />
                    <Box>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={60} height={14} />
                    </Box>
                  </Box>
                  <Skeleton variant="rounded" width={50} height={24} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Skeleton variant="rounded" width={60} height={24} />
                  <Skeleton variant="rounded" width={60} height={24} />
                  <Skeleton variant="rounded" width={60} height={24} />
                </Box>
              </Box>
            ))}
          </Box>

          {/* Sector Table */}
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box 
                key={i}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  py: 1,
                  borderBottom: i < 5 ? '1px solid #e2e8f0' : 'none'
                }}
              >
                <Skeleton variant="text" width={30} height={18} />
                <Skeleton variant="text" width={120} height={18} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" width={50} height={24} />
                <Skeleton variant="rounded" width={50} height={24} />
                <Skeleton variant="rounded" width={50} height={24} />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Portfolio Visualization Skeleton */}
        <Paper sx={{ mb: 2, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={140} height={22} />
              <Skeleton variant="text" width={180} height={14} />
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {/* Pie Chart Placeholder */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: 200,
              bgcolor: '#f8fafc',
              borderRadius: 2
            }}>
              <Skeleton variant="circular" width={160} height={160} />
            </Box>
            
            {/* Stats Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
              {[1, 2, 3, 4].map((i) => (
                <Box 
                  key={i}
                  sx={{ 
                    p: 1.5, 
                    bgcolor: '#f8fafc', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Skeleton variant="text" width={60} height={14} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={40} height={28} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={50} height={12} sx={{ mx: 'auto' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Layer 3: Stock Candidates Skeleton */}
        <Paper sx={{ mb: 2, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={160} height={22} />
              <Skeleton variant="text" width={220} height={14} />
            </Box>
          </Box>

          {/* Signal Tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} />
          </Box>

          {/* Stock Cards Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Box 
                key={i}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Box>
                      <Skeleton variant="text" width={50} height={20} />
                      <Skeleton variant="text" width={80} height={14} />
                    </Box>
                  </Box>
                  <Skeleton variant="rounded" width={50} height={24} />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1 }} />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="rounded" width={60} height={28} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Footer Skeleton */}
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Skeleton variant="text" width={200} height={14} sx={{ mx: 'auto' }} />
        </Box>
      </Container>
    </Box>
  );
};

export default SafeInvestSkeleton;
